import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { getTrainees } from "../_training.api";
import { useMemo } from "react";

export const TRAINEES_QUERY_KEY = "nhwm-trainees";

interface TraineeFilters {
  email: string;
  name: string;
  state: string;
  district: string;
  // time: string;
  // post: string;
}

function buildQueryString(filters: TraineeFilters) {
  const params = new URLSearchParams();
  if (filters.state) params.set("state", filters.state);
  if (filters.name.trim()) params.set("name", filters.name);
  if (filters.email.trim()) params.set("email", filters.email);
  if (filters.district) params.set("district", filters.district);
  // if (filters.post) params.set("post", filters.post);
  // if (filters.time) params.set("time", filters.time);
  return params.toString();
}

export function useTrainees() {
  const [searchParams] = useSearchParams();

  const email = searchParams.get("email") || "";
  const name = searchParams.get("name") || "";
  const state = searchParams.get("state") || "";
  const district = searchParams.get("district") || "";
    // time: searchParams.get("time") || "",
    // post: searchParams.get("post") || "",

  const pageSize = parseInt(searchParams.get("pageSize") || "25", 10);
  const currentPageNumber = parseInt(searchParams.get("page") || "1", 10);

  const filters = useMemo<TraineeFilters>(
    () => ({ email, name, state, district }),
    [email, name, state, district],
  );

  const [debouncedFilters] = useDebounce(filters, 500);
  const queryString = useMemo(
    () => buildQueryString(debouncedFilters),
    [debouncedFilters],
  );

  const query = useQuery({
    queryKey: [
      TRAINEES_QUERY_KEY,
      currentPageNumber,
      pageSize,
      debouncedFilters,
    ],
    queryFn: async () => {
      const response = await getTrainees({
        currentPageNumber,
        pageSize,
        queryString,
      });

      if (
        response.message?.toLowerCase() !== "success" ||
        !Array.isArray(response?.data?.trainings)
      ) {
        throw new Error("Failed to fetch trainees");
      }

      return response.data.trainings;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  });

  const trainees = query.data ?? [];

  return {
    ...query,
    trainees,
    pageSize,
    currentPageNumber,
    hasNextPage: trainees.length === pageSize,
    hasPrevPage: currentPageNumber > 1,
  };
}
